import apiClient from '../apiClient';

/**
 * Handle API errors consistently
 */
export const handleApiError = (error) => {
    if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
    }
    return error.message || 'An unexpected error occurred';
};

/**
 * Extracts the filename from a Content-Disposition header.
 * Falls back to the provided defaultName if the header is absent or unparseable.
 */
const getFilenameFromResponse = (response, defaultName) => {
    const disposition = response.headers?.['content-disposition'];
    if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
            return match[1].trim();
        }
    }
    return defaultName;
};

/**
 * Triggers a browser download for a blob response using the backend-provided filename.
 */
const triggerDownload = (response, defaultName) => {
    const filename = getFilenameFromResponse(response, defaultName);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Create or update a daily sales report
 */
export const createDailySalesReport = async (reportData) => {
    try {
        const response = await apiClient.post('/sales-reports', reportData);
        return response.data;
    } catch (error) {
        console.error('Error creating daily sales report:', error);
        throw error;
    }
};

/**
 * Get all daily sales reports with filters
 */
export const getDailySalesReports = async (params = {}) => {
    try {
        const response = await apiClient.get('/sales-reports/list', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching daily sales reports:', error);
        throw error;
    }
};

/**
 * Get a single daily sales report by public ID
 */
export const getDailySalesReport = async (publicId) => {
    try {
        const response = await apiClient.get(`/sales-reports/${publicId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching daily sales report:', error);
        throw error;
    }
};

/**
 * Delete a daily sales report
 */
export const deleteDailySalesReport = async (publicId) => {
    try {
        const response = await apiClient.delete(`/sales-reports/${publicId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting daily sales report:', error);
        throw error;
    }
};

export const downloadConsolidatedPdf = async (startDate, endDate, salesPersonPublicId, onlySalesStaff = false) => {
    try {
        const response = await apiClient.get('/sales-reports/consolidated/pdf', {
            params: { startDate, endDate, salesPersonPublicId, onlySalesStaff },
            responseType: 'blob'
        });
        triggerDownload(response, `Daily_Operations_Report_${startDate || 'all'}.pdf`);
        return true;
    } catch (error) {
        console.error('Error downloading consolidated PDF:', error);
        throw error;
    }
};

/**
 * Download Visited but Not Converted PDF
 */
export const downloadVisitedNotConvertedPdf = async (startDate, endDate, salesPersonPublicId, sort = 'desc') => {
    try {
        const response = await apiClient.get('/sales-reports/analytics/visited-not-converted/pdf', {
            params: { startDate, endDate, salesPersonPublicId, sort },
            responseType: 'blob'
        });
        triggerDownload(response, `Visited_Not_Converted_Prospects_${startDate || 'all'}.pdf`);
        return true;
    } catch (error) {
        console.error('Error downloading Visited but Not Converted PDF:', error);
        throw error;
    }
};

/**
 * Download Pending Payments PDF
 */
export const getGlobalPendingPayments = async (salesPersonPublicId = '') => {
    try {
        const response = await apiClient.get('/sales-reports/analytics/pending-payments/global', {
            params: salesPersonPublicId ? { salesPersonPublicId } : {}
        });
        return response.data || [];
    } catch (error) {
        console.error('Error fetching global pending payments:', error);
        throw error;
    }
};

export const downloadPendingPaymentsPdf = async (startDate, endDate, salesPersonPublicId, sort = 'desc', detailed = false) => {
    try {
        const response = await apiClient.get('/sales-reports/analytics/pending-payments/pdf', {
            params: { startDate, endDate, salesPersonPublicId, sort, detailed },
            responseType: 'blob'
        });
        triggerDownload(response, `Pending_Payments_by_Customer_${startDate || 'all'}.pdf`);
        return true;
    } catch (error) {
        console.error('Error downloading Pending Payments PDF:', error);
        throw error;
    }
};

/**
 * Download Customers by Revenue PDF
 */
export const downloadCustomersByRevenuePdf = async (startDate, endDate, salesPersonPublicId, sort = 'desc') => {
    try {
        const response = await apiClient.get('/sales-reports/analytics/revenue/pdf', {
            params: { startDate, endDate, salesPersonPublicId, sort },
            responseType: 'blob'
        });
        triggerDownload(response, `Customers_by_Revenue_${startDate || 'all'}.pdf`);
        return true;
    } catch (error) {
        console.error('Error downloading Customers by Revenue PDF:', error);
        throw error;
    }
};

/**
 * Download Salesperson Performance Leaderboard PDF
 */
export const downloadLeaderboardPdf = async (startDate, endDate, salesPersonPublicId, sortBy = 'revenue', sort = 'desc') => {
    try {
        const response = await apiClient.get('/sales-reports/analytics/leaderboard/pdf', {
            params: { startDate, endDate, salesPersonPublicId, sortBy, sort },
            responseType: 'blob'
        });
        triggerDownload(response, `Salesperson_Performance_Leaderboard_${startDate || 'all'}.pdf`);
        return true;
    } catch (error) {
        console.error('Error downloading Salesperson Performance Leaderboard PDF:', error);
        throw error;
    }
};

/**
 * Get upcoming follow-up reminders
 */
export const getFollowUpReminders = async (params = {}) => {
    try {
        const response = await apiClient.get('/sales-reports/follow-ups', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching follow-up reminders:', error);
        throw error;
    }
};

/**
 * Fetch customer/prospect activity history (visits & sales records)
 */
export const getCustomerActivity = async (query) => {
    try {
        const response = await apiClient.get('/sales-reports/customer-activity', { params: { query } });
        return response.data;
    } catch (error) {
        console.error('Error fetching customer activity:', error);
        throw error;
    }
};

/**
 * Record a single sale/collection from order delivery into daily sales report
 */
export const recordSale = async (recordData) => {
    try {
        const response = await apiClient.post('/sales-reports/record-sale', recordData);
        return response.data;
    } catch (error) {
        console.error('Error recording sale from delivery:', error);
        throw error;
    }
};

/**
 * Record a bulk customer B2B payment
 */
export const recordBulkCustomerPayment = async (paymentData) => {
    try {
        const response = await apiClient.post('/sales-reports/payments/bulk', paymentData);
        return response.data;
    } catch (error) {
        console.error('Error recording bulk customer payment:', error);
        throw error;
    }
};

export const dailySalesReportApi = {
    createDailySalesReport,
    getDailySalesReports,
    getDailySalesReport,
    deleteDailySalesReport,
    downloadConsolidatedPdf,
    downloadVisitedNotConvertedPdf,
    getGlobalPendingPayments,
    downloadPendingPaymentsPdf,
    downloadCustomersByRevenuePdf,
    downloadLeaderboardPdf,
    getFollowUpReminders,
    getCustomerActivity,
    recordBulkCustomerPayment,
    recordSale,
    handleApiError
};

export default dailySalesReportApi;
